import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Button, Typography, TextField, InputLabel, MenuItem, Select,
    RadioGroup, FormControlLabel, Radio, CircularProgress, useMediaQuery,
} from '@mui/material';
import { Upload, FileText } from 'lucide-react';
import Cookies from 'js-cookie';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useToast } from '../../providers/toaster-provider';
import theme from '../../util/theme/theme';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';
import { FolderOption, FolderOptionLabel } from './move-document';

export interface UploadDocumentProps {
    open: boolean;
    entityId: number | null;
    // Pre-selected target - the folder currently open on the Documents page.
    folderId: number | null;
    // Folders a document can be uploaded into (system folders excluded).
    folders: FolderOption[];
    onClose: () => void;
    onUploaded: () => void;
}

const ROOT_VALUE = '__root__';

const UploadDocument: React.FC<UploadDocumentProps> = ({ open, entityId, folderId, folders, onClose, onUploaded }) => {
    const { showSuccess, showError } = useToast();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [title, setTitle] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [accessType, setAccessType] = useState<'anonymous' | 'email_list'>('anonymous');
    const [isUploading, setIsUploading] = useState(false);
    const [target, setTarget] = useState<string>(ROOT_VALUE);

    useEffect(() => {
        if (open) setTarget(folderId && folders.some(f => f.id === folderId) ? String(folderId) : ROOT_VALUE);
    }, [open, folderId, folders]);

    const sortedFolders = useMemo(() => [...folders].sort((a, b) => a.path.localeCompare(b.path)), [folders]);

    const reset = () => {
        setTitle('');
        setFile(null);
        setAccessType('anonymous');
        setIsUploading(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];
        if (!selected) return;
        if (selected.type !== 'application/pdf') {
            showError('Only PDF files are accepted');
            return;
        }
        setFile(selected);
        if (!title) setTitle(selected.name.replace(/\.pdf$/i, ''));
    };

    const handleUpload = async () => {
        if (!title.trim() || !file || !entityId) return;
        setIsUploading(true);
        try {
            const { url, method } = buildApiRequestConfig(routes.documents.upload);
            const formData = new FormData();
            formData.append('title', title.trim());
            formData.append('type', 'pdf');
            formData.append('accessType', accessType);
            formData.append('entityId', String(entityId));
            // No folder chosen uploads to root.
            if (target !== ROOT_VALUE) formData.append('folderId', target);
            formData.append('file', file);

            const token = Cookies.get('token');
            const headers: HeadersInit = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;
            // Don't set Content-Type for FormData - the browser sets it with the boundary

            const response = await fetch(url, { method, body: formData, headers });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to upload document');
            }

            showSuccess('Document uploaded successfully');
            reset();
            onUploaded();
            onClose();
        } catch (error: any) {
            showError(error?.message || 'Failed to upload document');
        } finally {
            setIsUploading(false);
        }
    };

    const content = (
        <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 20, pb: 1 }}>Upload Document</DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box>
                        <InputLabel sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>PDF File</InputLabel>
                        <Box
                            component="label"
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5, p: 2,
                                border: '2px dashed #e0e0e0', borderRadius: 2, cursor: 'pointer',
                                background: 'var(--color-container-background)',
                                '&:hover': { borderColor: 'var(--color-primary)' },
                            }}
                        >
                            <input type="file" accept="application/pdf" hidden onChange={handleFileChange} />
                            {file ? <FileText size={22} color="var(--color-primary)" /> : <Upload size={22} color="var(--color-text-muted)" />}
                            <Typography sx={{ fontSize: 13, color: file ? 'var(--color-default-text)' : 'var(--color-text-muted)' }}>
                                {file ? file.name : 'Click to choose a PDF file'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box>
                        <InputLabel sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>Title</InputLabel>
                        <TextField
                            fullWidth size="small"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            placeholder="e.g. Signed Contract"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Box>

                    <Box>
                        <InputLabel sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>Folder (optional)</InputLabel>
                        <Select
                            fullWidth size="small"
                            value={target}
                            onChange={e => setTarget(e.target.value)}
                            sx={{ borderRadius: 2 }}
                        >
                            <MenuItem value={ROOT_VALUE}>
                                <FolderOptionLabel />
                            </MenuItem>
                            {sortedFolders.map(f => (
                                <MenuItem key={f.id} value={String(f.id)}>
                                    <FolderOptionLabel path={f.path} />
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box>
                        <InputLabel sx={{ fontSize: 13, mb: 0.5, fontWeight: 600 }}>Who can view</InputLabel>
                        <RadioGroup value={accessType} onChange={e => setAccessType(e.target.value as 'anonymous' | 'email_list')} row>
                            <FormControlLabel value="anonymous" control={<Radio size="small" sx={{ color: 'var(--color-primary)' }} />}
                                label={<Typography sx={{ fontSize: 13 }}>Anyone with the link</Typography>} />
                            <FormControlLabel value="email_list" control={<Radio size="small" sx={{ color: 'var(--color-primary)' }} />}
                                label={<Typography sx={{ fontSize: 13 }}>Invited people only</Typography>} />
                        </RadioGroup>
                        <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mt: 0.5 }}>
                            You can invite specific people after uploading, from the Manage screen.
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Button
                    onClick={handleClose}
                    variant="contained"
                    sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-container-background)' } }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={!title.trim() || !file || isUploading}
                    startIcon={isUploading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : undefined}
                    sx={{ background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 140, '&:hover': { background: 'var(--color-primary-hover)' } }}
                >
                    {isUploading ? 'Uploading…' : 'Upload'}
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={handleClose} fitContent>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            {content}
        </Dialog>
    );
};

export default UploadDocument;
